import type { User } from '../types/room'
import type { DetailedPrediction } from '../types/detailedPrediction'
import type { PlayerPredictionStats } from '../types/playerPredictionStats'
import type { BestPredictionFromApi, PlayedMatch } from '../services/matchService'
import type { FutureChampionPrediction } from '../types/futureChampion'
import type { TopScorerPrediction } from '../types/topScorer'

export const DEMO_ROOM_ID = 'demo-transparent-room'

export const DEMO_ROOM_NAME = 'Transparent'

const WC = 'WorldCup2026'

function match(
  id: string,
  teamA: string,
  teamB: string,
  date: string,
  time: string,
  score: string,
  winner: string | null,
): DetailedPrediction['match'] {
  return {
    _id: id,
    teamA,
    teamB,
    date,
    time,
    score,
    winner,
    league: WC,
  }
}

function pred(
  id: string,
  m: DetailedPrediction['match'],
  predictedScore: string,
  winner: string | null,
  winnerPts: number,
  scorePts: number,
): DetailedPrediction {
  return {
    _id: id,
    winner,
    predictedScore,
    match: m,
    predictedWinnerPoint: winnerPts,
    predictedScorePoint: scorePts,
  }
}

export const DEMO_PLAYERS: User[] = [
  { _id: 'demo-p1', name: 'Jamie Fox', predictions: [], totalPoints: 124, predictionCount: 168 },
  { _id: 'demo-p2', name: 'Riley Davis', predictions: [], totalPoints: 118, predictionCount: 165 },
  { _id: 'demo-p3', name: 'Quinn Miller', predictions: [], totalPoints: 112, predictionCount: 162 },
  { _id: 'demo-p4', name: 'Avery Clark', predictions: [], totalPoints: 98, predictionCount: 158 },
  { _id: 'demo-p5', name: 'Skyler White', predictions: [], totalPoints: 89, predictionCount: 155 },
  { _id: 'demo-p6', name: 'Morgan Hayes', predictions: [], totalPoints: 82, predictionCount: 150 },
  { _id: 'demo-p7', name: 'Drew Bennett', predictions: [], totalPoints: 76, predictionCount: 148 },
  { _id: 'demo-p8', name: 'Jordan Blake', predictions: [], totalPoints: 70, predictionCount: 145 },
  { _id: 'demo-p9', name: 'Taylor Reed', predictions: [], totalPoints: 64, predictionCount: 142 },
]

const PLAYED: DetailedPrediction[] = [
  pred('dp1', match('m1', 'Iran', 'New Zealand', '2026-06-16', '03:00:00', '2-2', null), '1-0', 'Iran', 0, 0),
  pred('dp2', match('m2', 'Saudi Arabia', 'Uruguay', '2026-06-16', '06:00:00', '1-1', null), '0-1', 'Uruguay', 1, 0),
  pred('dp3', match('m3', 'Belgium', 'Egypt', '2026-06-16', '09:00:00', '2-0', 'Belgium'), '2-0', 'Belgium', 1, 1),
  pred('dp4', match('m4', 'France', 'Senegal', '2026-06-16', '21:00:00', '3-1', 'France'), '2-1', 'France', 1, 0),
  pred('dp5', match('m5', 'Argentina', 'Algeria', '2026-06-17', '00:00:00', '1-0', 'Argentina'), '1-1', null, 0, 0),
]

const UPCOMING: DetailedPrediction[] = [
  pred('up1', match('u1', 'Iraq', 'Norway', '2026-06-17', '03:00:00', '', null), '1-0', 'Iraq', 0, 0),
  pred('up2', match('u2', 'Austria', 'Jordan', '2026-06-17', '06:00:00', '', null), '2-1', 'Austria', 0, 0),
  pred('up3', match('u3', 'Portugal', 'DR Congo', '2026-06-17', '09:00:00', '', null), '3-0', 'Portugal', 0, 0),
  pred('up4', match('u4', 'England', 'Croatia', '2026-06-17', '12:00:00', '', null), '2-2', null, 0, 0),
]

export function getDemoPlayerPredictions(playerId: string): DetailedPrediction[] {
  const idx = DEMO_PLAYERS.findIndex((p) => p._id === playerId)
  if (idx < 0) return [...PLAYED, ...UPCOMING]
  const playedSlice = PLAYED.slice(0, Math.max(2, 5 - Math.floor(idx / 2)))
  const upcomingSlice = UPCOMING.slice(0, Math.max(2, 4 - Math.floor(idx / 3)))
  return [...playedSlice, ...upcomingSlice]
}

export const DEMO_PLAYED_MATCHES: PlayedMatch[] = [
  { _id: 'm1', id: 'm1', teamA: 'Iran', teamB: 'New Zealand', date: '2026-06-16', time: '03:00:00', score: '2-2', winner: null, league: WC },
  { _id: 'm2', id: 'm2', teamA: 'Saudi Arabia', teamB: 'Uruguay', date: '2026-06-16', time: '06:00:00', score: '1-1', winner: null, league: WC },
  { _id: 'm3', id: 'm3', teamA: 'Belgium', teamB: 'Egypt', date: '2026-06-16', time: '09:00:00', score: '2-0', winner: 'Belgium', league: WC },
  { _id: 'm4', id: 'm4', teamA: 'France', teamB: 'Senegal', date: '2026-06-16', time: '21:00:00', score: '3-1', winner: 'France', league: WC },
  { _id: 'm5', id: 'm5', teamA: 'Argentina', teamB: 'Algeria', date: '2026-06-17', time: '00:00:00', score: '1-0', winner: 'Argentina', league: WC },
  { _id: 'm6', id: 'm6', teamA: 'Ivory Coast', teamB: 'Ecuador', date: '2026-06-17', time: '03:00:00', score: '0-0', winner: null, league: WC },
]

export const DEMO_UPCOMING_MATCHES: PlayedMatch[] = [
  { _id: 'u1', id: 'u1', teamA: 'Iraq', teamB: 'Norway', date: '2026-06-17', time: '03:00:00', score: '', winner: null, league: WC },
  { _id: 'u2', id: 'u2', teamA: 'Austria', teamB: 'Jordan', date: '2026-06-17', time: '06:00:00', score: '', winner: null, league: WC },
  { _id: 'u3', id: 'u3', teamA: 'Portugal', teamB: 'DR Congo', date: '2026-06-17', time: '09:00:00', score: '', winner: null, league: WC },
  { _id: 'u4', id: 'u4', teamA: 'England', teamB: 'Croatia', date: '2026-06-17', time: '12:00:00', score: '', winner: null, league: WC },
  { _id: 'u5', id: 'u5', teamA: 'Germany', teamB: 'Japan', date: '2026-06-17', time: '15:00:00', score: '', winner: null, league: WC },
]

export const DEMO_PLAY_MATCH = {
  _id: 'u3',
  teamA: 'Portugal',
  teamB: 'DR Congo',
  date: '2026-06-17',
  time: '09:00:00',
  league: WC,
  number: 'Group K',
  stepIndex: 2,
  totalSteps: 50,
}

function buildStats(rank: number, totalPts: number): PlayerPredictionStats {
  const played = 28 - rank
  const earned = Math.max(12, totalPts - 18)
  const possible = played * 3 + 35
  return {
    period: '30d',
    summary: {
      totalGames: played + 8,
      playedWithScore: played,
      earnedPoints: earned,
      possiblePoints: possible,
      efficiencyPct: Math.round((earned / possible) * 100),
      resultAccuracyPct: 38 + (9 - rank) * 2,
      scoreAccuracyPct: 8 + (9 - rank),
    },
    breakdown: {
      winners: { count: 14, correct: 7 + Math.max(0, 4 - rank), earned: 7 + Math.max(0, 4 - rank), possible: 14 },
      draws: { count: 6, correct: 2, earned: 6, possible: 18 },
      scores: { count: played, correct: 2 + Math.max(0, 3 - rank), earned: 2 + Math.max(0, 3 - rank), possible: played },
      extraTime: { count: 0, correct: 0, earned: 0, possible: 0 },
      penalties: { count: 0, correct: 0, earned: 0, possible: 0 },
    },
    extras: {
      futureChampion: { earned: rank <= 3 ? 0 : 0, possible: 20, events: 1 },
      topScorer: { earned: rank === 1 ? 0 : 0, possible: 15, events: 1 },
    },
    rankingTotalPoints: totalPts,
  }
}

export function getDemoPlayerStats(playerId: string): PlayerPredictionStats {
  const player = DEMO_PLAYERS.find((p) => p._id === playerId)
  const rank = DEMO_PLAYERS.findIndex((p) => p._id === playerId) + 1
  return buildStats(rank || 5, player?.totalPoints ?? 80)
}

export const DEMO_FUTURE_CHAMPION: FutureChampionPrediction[] = [
  {
    id: 'fc1',
    _id: 'fc1',
    roomId: DEMO_ROOM_ID,
    eventId: 'fce1',
    playerId: 'demo-p1',
    predictedChampion: 'Portugal',
    points: 0,
    createdAt: '',
    updatedAt: '',
    event: {
      id: 'fce1',
      name: 'World Cup 2026 champion',
      gameType: 'football',
      tournamentName: 'WorldCup2026',
      eventDate: '2026-07-19',
      choices: [],
      isActive: true,
      createdAt: '',
      updatedAt: '',
    },
  },
]

export const DEMO_TOP_SCORER: TopScorerPrediction[] = [
  {
    id: 'ts1',
    roomId: DEMO_ROOM_ID,
    eventId: 'tse1',
    playerId: 'demo-p1',
    predictedScorer: 'Kylian Mbappe',
    points: 0,
    event: {
      id: 'tse1',
      name: 'Top scorer',
      league: WC,
      gameType: 'football',
      eventDate: '2026-07-19',
      choices: [],
      isActive: true,
    },
  },
]

export const DEMO_BEST_PREDICTIONS: BestPredictionFromApi[] = [
  {
    _id: 'bp1',
    winner: 'France',
    predictedScore: '3-1',
    predictedWinnerPoint: 1,
    predictedScorePoint: 0,
    match: { _id: 'm4', teamA: 'France', teamB: 'Senegal', date: '2026-06-16', time: '21:00:00', score: '3-1', winner: 'France', league: WC },
    player: { _id: 'demo-p1', name: 'Jamie Fox' },
  },
  {
    _id: 'bp2',
    winner: 'Belgium',
    predictedScore: '2-0',
    predictedWinnerPoint: 1,
    predictedScorePoint: 1,
    match: { _id: 'm3', teamA: 'Belgium', teamB: 'Egypt', date: '2026-06-16', time: '09:00:00', score: '2-0', winner: 'Belgium', league: WC },
    player: { _id: 'demo-p2', name: 'Riley Davis' },
  },
  {
    _id: 'bp3',
    winner: 'Uruguay',
    predictedScore: '0-1',
    predictedWinnerPoint: 1,
    predictedScorePoint: 0,
    match: { _id: 'm2', teamA: 'Saudi Arabia', teamB: 'Uruguay', date: '2026-06-16', time: '06:00:00', score: '1-1', winner: null, league: WC },
    player: { _id: 'demo-p3', name: 'Quinn Miller' },
  },
]

export const DEMO_MATCH_PREDICTIONS_BY_MATCH: Record<string, Array<{
  playerName: string
  predictedScore: string
  winner: string | null
  resultPts: number
  scorePts: number
  total: number
}>> = {
  m4: [
    { playerName: 'Jamie Fox', predictedScore: '3-1', winner: 'France', resultPts: 1, scorePts: 0, total: 1 },
    { playerName: 'Riley Davis', predictedScore: '2-0', winner: 'France', resultPts: 1, scorePts: 0, total: 1 },
    { playerName: 'Quinn Miller', predictedScore: '1-1', winner: null, resultPts: 0, scorePts: 0, total: 0 },
    { playerName: 'Avery Clark', predictedScore: '2-1', winner: 'France', resultPts: 1, scorePts: 0, total: 1 },
  ],
  m3: [
    { playerName: 'Jamie Fox', predictedScore: '2-0', winner: 'Belgium', resultPts: 1, scorePts: 1, total: 2 },
    { playerName: 'Riley Davis', predictedScore: '1-0', winner: 'Belgium', resultPts: 1, scorePts: 0, total: 1 },
    { playerName: 'Skyler White', predictedScore: '1-1', winner: null, resultPts: 0, scorePts: 0, total: 0 },
  ],
}
